import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useNavigate, useSearchParams } from 'react-router';
import {
  CampaignCreatePresenter,
  type CampaignCreateFormData,
  type CampaignCreateTouchedFields,
  type CampaignCreateValidationErrors,
} from '@/components/campaigns/campaign-create.presenter';
import { toast } from '@/components/ui/sonner';
import { TemplateString } from '@/components/template-string';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAudiences } from '@/hooks/use-audiences';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import { useSelf } from '@/hooks/use-self';
import { useTenant } from '@/hooks/use-tenant';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';
import { sendTestNotification, SendNotificationDto } from '@/lib/api-client';
import { campaignsControllerCreateCampaign } from '@/generated/korza-api-client';
import type { CreateCampaignDto } from '@/generated/korza-api-client/types.gen';
import {
  generateUtmFromName,
  validateDateRule,
  validateLinkRule,
  validateRequiredTextRule,
  validateTimeRule,
  validateUtmCampaignRule,
} from '@/lib/campaign-fields';
import { getLuxonLocale } from '@/lib/utils';

class CreateCampaignError extends Error {
  constructor(
    message: string,
    public readonly messageCode?: string,
    public readonly status?: number
  ) {
    super(message);
    this.name = 'CreateCampaignError';
  }
}

const maskTime = (value: string) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
};

const buildSuggestedCampaignName = (
  title: string,
  launchDate: Date,
  locale: Locales
) => {
  const date = DateTime.fromJSDate(launchDate)
    .setLocale(getLuxonLocale(locale))
    .toLocaleString({ year: 'numeric', month: '2-digit', day: '2-digit' });
  return `"${title.trim()}" - ${date}`;
};

type CampaignCreateStepKind = 'message' | 'schedule' | 'audience' | 'tracking';

const buildCampaignCreateStepKinds = (
  audiencesEnabled: boolean
): CampaignCreateStepKind[] =>
  audiencesEnabled
    ? ['message', 'schedule', 'audience', 'tracking']
    : ['message', 'schedule', 'tracking'];

export default function CreateCampaignV2Page() {
  const navigate = useNavigate();
  const { LL, locale } = useI18nContext();
  const [searchParams] = useSearchParams();
  const initialDateValue = searchParams.get('date');
  const initialAudienceIdParam = searchParams.get('audienceId');
  const { data: audiencesData } = useAudiences();
  const audiencesEnabled = useFeatureFlags().data?.audiencesEnabled ?? false;
  const stepKinds = buildCampaignCreateStepKinds(audiencesEnabled);

  const [isTestDialogOpen, setIsTestDialogOpen] = useState(false);
  const [formData, setFormData] = useState<CampaignCreateFormData>(() => {
    const defaultName = LL.createCampaign.defaultName();
    return {
      name: defaultName,
      title: '',
      body: '',
      link: '',
      linkDestination: 'home',
      launchDate: initialDateValue
        ? DateTime.fromISO(initialDateValue).toJSDate()
        : undefined,
      launchTime: '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      utmCampaign: generateUtmFromName(defaultName),
      audienceId: null,
      suppressRecentNotifications: true,
    };
  });

  const [validationErrors, setValidationErrors] =
    useState<CampaignCreateValidationErrors>({
      name: '',
      title: '',
      body: '',
      link: '',
      launchDate: '',
      launchTime: '',
      utmCampaign: '',
      audienceId: '',
    });

  const [touchedFields, setTouchedFields] =
    useState<CampaignCreateTouchedFields>({
      name: false,
      title: false,
      body: false,
      link: false,
      launchDate: false,
      launchTime: false,
      timezone: false,
      utmCampaign: false,
      audienceId: false,
    });

  const [utmCampaignEdited, setUtmCampaignEdited] = useState(false);
  const [nameEdited, setNameEdited] = useState(false);
  const [isSaveNameDialogOpen, setIsSaveNameDialogOpen] = useState(false);
  const [saveNameDialogName, setSaveNameDialogName] = useState('');

  const self = useSelf();
  const tenant = useTenant();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!audiencesEnabled || !audiencesData || !initialAudienceIdParam) return;

    const isValidAudience = audiencesData.some(
      (audience) => audience.id === initialAudienceIdParam
    );

    if (!isValidAudience) return;

    setFormData((prev) => ({ ...prev, audienceId: initialAudienceIdParam }));
    setTouchedFields((prev) => ({ ...prev, audienceId: true }));
  }, [audiencesEnabled, audiencesData, initialAudienceIdParam]);

  const validateName = (nameStr: string) =>
    validateRequiredTextRule(nameStr)
      ? LL.createCampaign.validation.campaignNameRequired()
      : '';

  const validateTitle = (titleStr: string) =>
    validateRequiredTextRule(titleStr)
      ? LL.createCampaign.validation.notificationTitleRequired()
      : '';

  const validateBody = (bodyStr: string) =>
    validateRequiredTextRule(bodyStr)
      ? LL.createCampaign.validation.notificationBodyRequired()
      : '';

  const validateLink = (
    linkStr: string,
    linkDestination: CampaignCreateFormData['linkDestination'] = formData.linkDestination
  ) => {
    if (linkDestination === 'home') {
      return '';
    }

    switch (validateLinkRule(linkStr, tenant.storeWebsiteDomain)) {
      case null:
        return '';
      case 'required':
        return LL.createCampaign.validation.notificationLinkRequired();
      case 'websiteDomainNotConfigured':
        return LL.createCampaign.validation.websiteDomainNotConfigured();
      case 'linkInvalid':
      case 'linkNotFromStore':
        return LL.createCampaign.validation.linkMustStartWith({
          prefix: `https://${tenant.storeWebsiteDomain}`,
        });
    }
  };

  const validateDate = (date: Date | undefined) => {
    switch (validateDateRule(date, DateTime.now())) {
      case null:
        return '';
      case 'required':
        return LL.createCampaign.validation.launchDateRequired();
      case 'dateInvalid':
        return LL.createCampaign.validation.validDateRequired();
      case 'dateInPast':
        return LL.createCampaign.validation.launchDateInPast();
      case 'dateTooFar':
        return LL.createCampaign.validation.launchDateTooFarInFuture();
    }
  };

  const validateTime = (timeStr: string, launchDate = formData.launchDate) => {
    switch (validateTimeRule(timeStr, launchDate, DateTime.now())) {
      case null:
        return '';
      case 'required':
        return LL.createCampaign.validation.launchTimeRequired();
      case 'timeIncomplete':
        return LL.createCampaign.validation.validTimeRequired();
      case 'timeOutOfRange':
        return LL.createCampaign.validation.validTimeRange();
      case 'timeInPast':
        return LL.createCampaign.validation.launchTimeInFuture();
    }
  };

  const validateUtmCampaign = (value: string) =>
    validateUtmCampaignRule(value)
      ? LL.common.validation.thisFieldIsRequired()
      : '';

  const validateAudienceId = (_audienceId: string | null) => '';

  const createCampaignMutation = useMutation({
    mutationFn: async (data: CreateCampaignDto) => {
      const res = await campaignsControllerCreateCampaign({
        path: { tenantSlug: tenant.slug },
        body: data,
      });
      if (res.error || !res.data) {
        const body = res.error as
          | { message?: unknown; messageCode?: unknown }
          | undefined;
        const message =
          typeof body?.message === 'string'
            ? body.message
            : LL.createCampaign.toast.failedToCreateCampaign();
        const messageCode =
          typeof body?.messageCode === 'string' ? body.messageCode : undefined;
        throw new CreateCampaignError(
          message,
          messageCode,
          res.response?.status
        );
      }
      return res.data;
    },
    onSuccess: (_data, variables) => {
      toast.success(LL.createCampaign.toast.campaignCreatedTitle(), {
        description: LL.createCampaign.toast.campaignCreatedDescription({
          campaignName: variables.name,
        }),
        duration: 3000,
      });
      navigate(`/${tenant.slug}/campaigns`);
    },
    onError: (error) => {
      const isUtmAlreadyUsed =
        error instanceof CreateCampaignError &&
        error.status === 400 &&
        error.messageCode === 'utmCampaignValueAlreadyUsed';
      toast.error(LL.general.error(), {
        description: isUtmAlreadyUsed ? (
          <TemplateString
            templateString={LL.createCampaign.toast.utmCampaignValueAlreadyUsed()}
            componentMap={{
              UtmFieldLabel: () => (
                <em>
                  {LL.createCampaign.toast.utmCampaignValueAlreadyUsedFieldLabel()}
                </em>
              ),
            }}
          />
        ) : error instanceof Error ? (
          error.message
        ) : (
          LL.createCampaign.toast.failedToCreateCampaign()
        ),
        duration: 3000,
      });
    },
  });

  const sendTestNotificationMutation = useMutation({
    mutationFn: (data: SendNotificationDto) =>
      sendTestNotification(tenant.slug, data),
    onSuccess: () => {
      toast.success(LL.createCampaign.toast.testNotificationSentTitle(), {
        description: LL.createCampaign.toast.testNotificationSentDescription(),
        duration: 3000,
      });
      setIsTestDialogOpen(false);
    },
    onError: (error) => {
      toast.error(LL.general.error(), {
        description:
          error instanceof Error
            ? error.message
            : LL.createCampaign.toast.failedToSendTestNotification(),
        duration: 3000,
      });
    },
  });

  const handleFieldChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const newValue = name === 'launchTime' ? maskTime(value) : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleFieldBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (!touchedFields[name as keyof CampaignCreateTouchedFields]) {
      setTouchedFields((prev) => ({ ...prev, [name]: true }));
    }

    if (name === 'utmCampaign') {
      setUtmCampaignEdited(true);
    }

    if (name === 'title') {
      setValidationErrors((prev) => ({ ...prev, title: validateTitle(value) }));
    } else if (name === 'body') {
      setValidationErrors((prev) => ({ ...prev, body: validateBody(value) }));
    } else if (name === 'link') {
      setValidationErrors((prev) => ({
        ...prev,
        link: validateLink(value, formData.linkDestination),
      }));
    } else if (name === 'launchTime') {
      setValidationErrors((prev) => ({
        ...prev,
        launchTime: validateTime(value),
      }));
    } else if (name === 'utmCampaign') {
      setValidationErrors((prev) => ({
        ...prev,
        utmCampaign: validateUtmCampaign(value),
      }));
    }
  };

  const handleDateChange = (date: Date | undefined) => {
    setFormData((prev) => ({ ...prev, launchDate: date }));

    if (!touchedFields.launchDate) {
      setTouchedFields((prev) => ({ ...prev, launchDate: true }));
    }

    setValidationErrors((prev) => ({
      ...prev,
      launchDate: validateDate(date),
      launchTime: validateTime(formData.launchTime, date),
    }));
  };

  const handleTimezoneChange = (timezone: string) => {
    setFormData((prev) => ({ ...prev, timezone }));
  };

  const handleAudienceChange = (audienceId: string | null) => {
    setFormData((prev) => ({ ...prev, audienceId }));
    setValidationErrors((prev) => ({
      ...prev,
      audienceId: validateAudienceId(audienceId),
    }));
  };

  const handleAudienceBlur = () => {
    if (!touchedFields.audienceId) {
      setTouchedFields((prev) => ({ ...prev, audienceId: true }));
    }
    setValidationErrors((prev) => ({
      ...prev,
      audienceId: validateAudienceId(formData.audienceId),
    }));
  };

  const handleSuppressChange = (checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      suppressRecentNotifications: checked,
    }));
  };

  const handleNameChange = (name: string) => {
    setNameEdited(true);
    setFormData((prev) => {
      const updated = { ...prev, name };
      if (!utmCampaignEdited) {
        updated.utmCampaign = generateUtmFromName(name);
      }
      return updated;
    });
    setTouchedFields((prev) => ({ ...prev, name: true }));
    setValidationErrors((prev) => ({ ...prev, name: validateName(name) }));
  };

  const submitCampaign = (name: string) => {
    if (!formData.launchDate) return;

    const [hours, minutes] = formData.launchTime.split(':');
    const launchDateTime =
      DateTime.fromObject(
        {
          year: formData.launchDate.getFullYear(),
          month: formData.launchDate.getMonth() + 1,
          day: formData.launchDate.getDate(),
          hour: parseInt(hours),
          minute: parseInt(minutes),
        },
        { zone: formData.timezone }
      )
        .setZone(formData.timezone)
        .toISO() || '';

    const notificationLink =
      formData.linkDestination === 'home'
        ? `https://${tenant.storeWebsiteDomain}/`
        : formData.link;

    const utmCampaign = utmCampaignEdited
      ? formData.utmCampaign
      : generateUtmFromName(name);

    const campaignData: CreateCampaignDto = {
      name,
      launchDateTime,
      notificationTitle: formData.title,
      notificationBody: formData.body,
      notificationLink,
      utmCampaign,
      ...(audiencesEnabled && formData.audienceId
        ? { audienceId: formData.audienceId }
        : {}),
    };

    createCampaignMutation.mutate(campaignData);
  };

  const handleSaveNameConfirm = (name: string) => {
    const trimmedName =
      name.trim() ||
      (formData.launchDate
        ? buildSuggestedCampaignName(
            formData.title,
            formData.launchDate,
            locale as Locales
          )
        : formData.name);

    handleNameChange(trimmedName);
    setIsSaveNameDialogOpen(false);
    submitCampaign(trimmedName);
  };

  const handleLinkDestinationChange = (
    linkDestination: CampaignCreateFormData['linkDestination']
  ) => {
    setFormData((prev) => ({ ...prev, linkDestination }));

    if (linkDestination === 'home') {
      setValidationErrors((prev) => ({ ...prev, link: '' }));
      return;
    }

    if (touchedFields.link) {
      setValidationErrors((prev) => ({
        ...prev,
        link: validateLink(formData.link, 'custom'),
      }));
    }
  };

  const handleSubmit = () => {
    setTouchedFields({
      name: true,
      title: true,
      body: true,
      link: true,
      launchDate: true,
      launchTime: true,
      timezone: true,
      utmCampaign: true,
      audienceId: true,
    });

    const nameError = validateName(formData.name);
    const titleError = validateTitle(formData.title);
    const bodyError = validateBody(formData.body);
    const linkError = validateLink(formData.link, formData.linkDestination);
    const dateError = validateDate(formData.launchDate);
    const timeError = validateTime(formData.launchTime);
    const utmError = validateUtmCampaign(formData.utmCampaign);
    const audienceError = validateAudienceId(formData.audienceId);

    setValidationErrors({
      name: nameError,
      title: titleError,
      body: bodyError,
      link: linkError,
      launchDate: dateError,
      launchTime: timeError,
      utmCampaign: utmError,
      audienceId: audienceError,
    });

    if (
      nameError ||
      titleError ||
      bodyError ||
      linkError ||
      dateError ||
      timeError ||
      utmError ||
      audienceError ||
      !formData.launchDate
    ) {
      return;
    }

    if (!nameEdited) {
      setSaveNameDialogName(
        buildSuggestedCampaignName(
          formData.title,
          formData.launchDate,
          locale as Locales
        )
      );
      setIsSaveNameDialogOpen(true);
      return;
    }

    submitCampaign(formData.name);
  };

  const handleSendTestNotification = () => {
    let websitePath = '/';

    if (formData.linkDestination === 'custom') {
      const escapedStoreWebsiteDomain = tenant.storeWebsiteDomain.replace(
        /[/\-\\^$*+?.()|[\]{}]/g,
        '\\$&'
      );
      websitePath =
        new RegExp(`^https?://${escapedStoreWebsiteDomain}/(.+)$`).exec(
          formData.link
        )?.[1] ?? '/';
    }

    const testNotificationData: SendNotificationDto = {
      title: formData.title,
      body: formData.body,
      websitePath,
    };

    sendTestNotificationMutation.mutate(testNotificationData);
  };

  const canSendTest = Boolean(
    formData.title &&
      formData.body &&
      (formData.linkDestination === 'home' ||
        !validateLink(formData.link, 'custom'))
  );

  const isStepValid = (stepIndex: number) => {
    switch (stepKinds[stepIndex]) {
      case 'message':
        return (
          !validateTitle(formData.title) &&
          !validateBody(formData.body) &&
          !validateLink(formData.link, formData.linkDestination)
        );
      case 'schedule':
        return (
          !validateDate(formData.launchDate) &&
          !validateTime(formData.launchTime)
        );
      case 'audience':
        return !validateAudienceId(formData.audienceId);
      case 'tracking':
        return !validateUtmCampaign(formData.utmCampaign);
      default:
        return false;
    }
  };

  const validateStep = (stepIndex: number) => {
    switch (stepKinds[stepIndex]) {
      case 'message':
        setTouchedFields((prev) => ({
          ...prev,
          title: true,
          body: true,
          link: true,
        }));
        setValidationErrors((prev) => ({
          ...prev,
          title: validateTitle(formData.title),
          body: validateBody(formData.body),
          link: validateLink(formData.link, formData.linkDestination),
        }));
        break;
      case 'schedule':
        setTouchedFields((prev) => ({
          ...prev,
          launchDate: true,
          launchTime: true,
        }));
        setValidationErrors((prev) => ({
          ...prev,
          launchDate: validateDate(formData.launchDate),
          launchTime: validateTime(formData.launchTime),
        }));
        break;
      case 'audience':
        setTouchedFields((prev) => ({ ...prev, audienceId: true }));
        setValidationErrors((prev) => ({
          ...prev,
          audienceId: validateAudienceId(formData.audienceId),
        }));
        break;
      case 'tracking':
        setTouchedFields((prev) => ({ ...prev, utmCampaign: true }));
        setValidationErrors((prev) => ({
          ...prev,
          utmCampaign: validateUtmCampaign(formData.utmCampaign),
        }));
        break;
    }

    return isStepValid(stepIndex);
  };

  const isFormValid =
    !validateName(formData.name) &&
    !validateTitle(formData.title) &&
    !validateBody(formData.body) &&
    !validateLink(formData.link, formData.linkDestination) &&
    !validateDate(formData.launchDate) &&
    !validateTime(formData.launchTime) &&
    !validateUtmCampaign(formData.utmCampaign) &&
    (!audiencesEnabled || !validateAudienceId(formData.audienceId));

  return (
    <CampaignCreatePresenter
      tenantSlug={tenant.slug}
      locale={locale as Locales}
      iconUrl={tenant.iconUrl}
      storeWebsiteDomain={tenant.storeWebsiteDomain}
      appScheme={tenant.appScheme}
      adminUserId={self.id}
      formData={formData}
      validationErrors={validationErrors}
      touchedFields={touchedFields}
      isMobile={isMobile}
      isTestDialogOpen={isTestDialogOpen}
      onTestDialogOpenChange={setIsTestDialogOpen}
      isCreating={createCampaignMutation.isPending}
      isSendingTest={sendTestNotificationMutation.isPending}
      isFormValid={isFormValid}
      isStepValid={isStepValid}
      onValidateStep={validateStep}
      canSendTest={canSendTest}
      onFieldChange={handleFieldChange}
      onFieldBlur={handleFieldBlur}
      onDateChange={handleDateChange}
      onTimezoneChange={handleTimezoneChange}
      onAudienceChange={handleAudienceChange}
      onAudienceBlur={handleAudienceBlur}
      onSuppressChange={handleSuppressChange}
      onNameChange={handleNameChange}
      onLinkDestinationChange={handleLinkDestinationChange}
      isSaveNameDialogOpen={isSaveNameDialogOpen}
      onSaveNameDialogOpenChange={setIsSaveNameDialogOpen}
      saveNameDialogName={saveNameDialogName}
      onSaveNameConfirm={handleSaveNameConfirm}
      onSubmit={handleSubmit}
      onOpenTestDialog={() => setIsTestDialogOpen(true)}
      onSendTestNotification={handleSendTestNotification}
      audiencesEnabled={audiencesEnabled}
    />
  );
}
